// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { confirmBillingPlanResponseSchema } from '@/billing/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const responseSchema = z.object({
  data: confirmBillingPlanResponseSchema,
  metadata: responseMetadataSchema,
});

// Sparse label override keyed by charge key. Cascade math owns the
// service period and billable dates; only the user-visible label
// flows through this list.
interface ConfirmBillingPlanChargeLabelOverride {
  key: string;
  label: string;
}

export interface ConfirmBillingPlanInput {
  projectKey: string;
  lineItemKey: string;
  startOn: DateTime;
  endOn: DateTime;
  activationDate: DateTime;
  // Empty / omitted leaves every charge's existing label intact.
  chargeLabelOverrides: ConfirmBillingPlanChargeLabelOverride[];
}

export async function confirmBillingPlan(input: ConfirmBillingPlanInput) {
  const url = `/v1/billing-plan/${input.projectKey}/${input.lineItemKey}/confirm`;
  const response = await api.post(url, {
    startOn: serializeDateForAPI(input.startOn),
    endOn: serializeDateForAPI(input.endOn),
    activationDate: serializeDateForAPI(input.activationDate),
    chargeLabelOverrides: input.chargeLabelOverrides,
  });
  const result = await parseResponse(responseSchema, response.data, {
    schemaName: 'ConfirmBillingPlan',
    endpoint: url,
  });
  return result.data;
}
